import { BoxOfferDraftEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import {
  BoxOfferInvitationStatus,
  BoxOfferType,
  OfferAutonomousPurchase,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { type BoxOfferApiGateway } from "@modules/boxOffer/gateway/boxOfferApi.gateway"
import {
  type BoxOfferDetailedDto,
  type BoxOfferItemDto,
  type BoxOfferMobileDetailedDto,
  type LightBoxOfferInfoDto,
  type MobileBoxOfferItemDto,
  StatusFilter,
} from "@modules/boxOffer/gateway/boxOfferDto"
import { ProgramType } from "@modules/programs/types/programTypes"
import { v4 as uuidv4 } from "uuid"

const BOX_OFFER_DETAILED_SUBSCRIPTION: BoxOfferDetailedDto = {
  id: "box_offer_subscription",
  type: BoxOfferType.Subscription,
  name: "Sample Subscription Offer",
  description: "This is a sample subscription offer.",
  maxWeeklyParticipation: 5,
  showcaseDisplay: true,
  priceDisplay: true,
  autonomousPurchase: OfferAutonomousPurchase.PUBLIC,
  programs: ["Program A", "Program B"],
  rules: [],
  validityPeriodMonths: 12,
  renewable: true,
  priceObject: {
    value: 29.99,
    currency: "EUR",
  },
  archived: false,
  currency: "EUR",
  minimumCommitmentPeriod: null,
  commitmentPeriodAutoRenewal: false,
  count: 0,
  isFree: false,
  recurringPeriod: "P1M",
  displayOrder: null,
}

const BOX_OFFER_ITEM_SUBSCRIPTION: BoxOfferItemDto = {
  id: "box_offer_subscription",
  type: BoxOfferType.Subscription,
  name: "Sample Subscription Offer",
  description: "This is a sample subscription offer.",
  priceObject: {
    value: 29.99,
    currency: "EUR",
  },
  minimumCommitmentPeriod: null,
  commitmentPeriodAutoRenewal: null,
  count: null,
  showcaseDisplay: true,
  priceDisplay: true,
  autonomousPurchase: OfferAutonomousPurchase.PUBLIC,
  validityPeriodMonths: 12,
  renewable: true,
  archived: false,
  price: 29.99,
  currency: "EUR",
  isFree: false,
  recurringPeriod: "P1M",
  displayOrder: null,
  activeMembers: 10,
  rules: [],
}

export const BOX_OFFER_TEST_DATA = {
  BOX_OFFER_ITEM_SUBSCRIPTION,
  BOX_OFFER_DETAILED_SUBSCRIPTION,
}

export const LIGHT_BOX_OFFER_SUBSCRIPTION: LightBoxOfferInfoDto = {
  id: "box_offer_subscription",
  type: BoxOfferType.Subscription,
  name: "Sample Subscription Offer",
  price: {
    value: 29.99,
    currency: "EUR",
  },
  recurringPeriod: "P1M",
}

export const LIGHT_BOX_OFFER_TEST_DATA = {
  LIGHT_BOX_OFFER_SUBSCRIPTION,
}

export class FakeBoxOfferApi implements BoxOfferApiGateway {
  boxOffersDetailed: BoxOfferDetailedDto[] = []
  boxOffersItems: BoxOfferItemDto[] = []

  givenBoxOffers(boxOffersDetailed: BoxOfferDetailedDto[]) {
    this.boxOffersDetailed = boxOffersDetailed
  }

  givenBoxOffersItems(boxOffersItems: BoxOfferItemDto[]) {
    this.boxOffersItems = boxOffersItems
  }

  getBoxOffers(options?: { onlyWithPrice?: boolean; statusFilter?: StatusFilter }) {
    if (options?.onlyWithPrice)
      return Promise.resolve(this.boxOffersItems.filter((boxOfferItem) => !!boxOfferItem.priceObject))
    else return Promise.resolve(this.boxOffersItems)
  }

  getBoxOffer(id: string): Promise<BoxOfferDetailedDto> {
    const index = this.boxOffersDetailed.findIndex((offer) => offer.id === id)

    if (index >= 0) return Promise.resolve(this.boxOffersDetailed[index]!)
    else throw new Error("Missing box offer")
  }

  createBoxOffer(boxOfferDraft: BoxOfferDraftEntity): Promise<void> {
    const dto: BoxOfferDetailedDto = {
      ...boxOfferDraft,
      id: uuidv4(),
      maxWeeklyParticipation: 2,
      archived: false,
      priceObject: { value: 0, currency: "EUR" },
      currency: "EUR",
      minimumCommitmentPeriod: null,
      validityPeriodMonths: null,
      isFree: false,
      count: 0,
      renewable: null,
      rules: boxOfferDraft.rules || [],
      commitmentPeriodAutoRenewal: false,
      recurringPeriod: "P1M",
      displayOrder: null,
    }
    this.boxOffersDetailed.push(dto)

    return Promise.resolve()
  }

  updateBoxOffer(id: string, updatedOfferDraft: BoxOfferDraftEntity) {
    const index = this.boxOffersDetailed.findIndex((offer) => offer.id === id)
    const dto: BoxOfferDetailedDto = {
      ...updatedOfferDraft,
      id,
      maxWeeklyParticipation: 2,
      archived: false,
      priceObject: updatedOfferDraft.price ? { value: updatedOfferDraft.price, currency: "EUR" } : null,
      currency: updatedOfferDraft.currency || "EUR",
      validityPeriodMonths: updatedOfferDraft.validityPeriodMonths || null,
      isFree: updatedOfferDraft.price ? false : true,
      count: updatedOfferDraft.count || 0,
      renewable: updatedOfferDraft.renewable || null,
      rules: updatedOfferDraft.rules || [],
      minimumCommitmentPeriod: null,
      commitmentPeriodAutoRenewal: updatedOfferDraft.commitmentPeriodAutoRenewal || null,
      recurringPeriod: updatedOfferDraft.recurringPeriod || null,
      displayOrder: null,
    }

    if (index !== -1) {
      this.boxOffersDetailed[index] = dto
    }

    return Promise.resolve()
  }

  deleteBoxOffer(id: string) {
    this.boxOffersDetailed = this.boxOffersDetailed.filter((offer) => offer.id !== id)
    return Promise.resolve()
  }

  sendBoxOfferInvitation() {
    return Promise.resolve()
  }

  getBoxOfferInvitations(userId: string) {
    return Promise.resolve([
      {
        id: userId,
        status: BoxOfferInvitationStatus.PENDING,
        boxOfferId: "box_offer_subscription",
        boxId: "boxId1",
        invitee: {
          id: "inviteeId1",
          username: "inviteeUsername1",
          firstname: "inviteeFirstname1",
          lastname: "inviteeLastname1",
          avatar: { url: "sampleUrl1", hash: "sampleHash1" },
        },
        inviter: {
          id: "inviterId1",
          username: "inviterUsername1",
          firstname: "inviterFirstname1",
          lastname: "inviterLastname1",
          avatar: { url: "sampleUrl1", hash: "sampleHash1" },
        },
        createdAt: "2021-01-01T00:00:00Z",
        coupon: null,
        deferredDate: null,
        updatedAtDate: null,
        subscriptionEndDate: null,
        outOfBand: false,
      },
    ])
  }

  cancelBoxOfferInvitation(invitationId: string) {
    return Promise.resolve({
      id: invitationId,
      status: BoxOfferInvitationStatus.CANCELLED,
      boxOfferId: "sampleBoxOfferId",
      boxId: "sampleBoxId",
      invitee: {
        id: "inviteeId",
        username: "inviteeUsername",
        firstname: "inviteeFirstname",
        lastname: "inviteeLastname",
        avatar: { url: "sampleUrl", hash: "sampleHash" },
      },
      inviter: {
        id: "inviterId",
        username: "inviterUsername",
        firstname: "inviterFirstname",
        lastname: "inviterLastname",
        avatar: { url: "sampleUrl", hash: "sampleHash" },
      },
      createdAt: "2021-01-01T00:00:00Z",
      coupon: null,
      deferredDate: null,
      updatedAtDate: null,
      outOfBand: false,
      subscriptionEndDate: null,
    })
  }

  getBoxOfferByUser(_: string, offerId: string): Promise<BoxOfferMobileDetailedDto> {
    return Promise.resolve({
      id: offerId,
      type: BoxOfferType.Subscription,
      name: "Sample Subscription Offer",
      description: "This is a sample subscription offer.",
      maxWeeklyParticipation: 5,
      showcaseDisplay: true,
      priceDisplay: true,
      autonomousPurchase: OfferAutonomousPurchase.PUBLIC,
      programs: [
        {
          id: "programA",
          name: "Program A",
          image: null,
          type: ProgramType.IN_BOX,
        },
      ],
      rules: [],
      validityPeriodMonths: 12,
      renewable: true,
      priceObject: {
        value: 29.99,
        currency: "EUR",
      },
      archived: false,
      currency: "EUR",
      minimumCommitmentPeriod: null,
      commitmentPeriodAutoRenewal: false,
      count: 0,
      isFree: false,
      canUserPurchase: true,
      hasAlreadyPurchase: false,
      invitation: null,
      recurringPeriod: "P1M",
    })
  }

  getBoxOffersByUser(_: string): Promise<MobileBoxOfferItemDto[]> {
    return Promise.resolve([])
  }
  subscribeFree(_: string, __: string): Promise<void> {
    return Promise.resolve()
  }

  createBoxOfferPaymentSubscription(_: string, __: string, ___: string): Promise<{ url: string }> {
    return Promise.resolve({ url: "https://example.com/payment" })
  }

  refuseOfferInvitation(invitationId: string) {
    return Promise.resolve({
      id: invitationId,
      status: BoxOfferInvitationStatus.CANCELLED,
      boxOfferId: "sampleBoxOfferId",
      boxId: "sampleBoxId",
      invitee: {
        id: "inviteeId",
        username: "inviteeUsername",
        firstname: "inviteeFirstname",
        lastname: "inviteeLastname",
        avatar: { url: "sampleUrl", hash: "sampleHash" },
      },
      inviter: {
        id: "inviterId",
        username: "inviterUsername",
        firstname: "inviterFirstname",
        lastname: "inviterLastname",
        avatar: { url: "sampleUrl", hash: "sampleHash" },
      },
      createdAt: "2021-01-01T00:00:00Z",
      coupon: null,
      deferredDate: null,
      updatedAtDate: null,
      outOfBand: false,
      subscriptionEndDate: null,
    })
  }

  arrangeOffersDisplayOrder(_orderMap: Record<string, number>): Promise<void> {
    return Promise.resolve()
  }
}
